import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { axios } from '../../libs/axios';

const UNCATEGORIZED = '—';
const MAX_FIELDS = 3;

function groupFields(fields) {
	const groups = new Map();
	fields.forEach((field) => {
		const category = field.category?.trim() || UNCATEGORIZED;
		if (!groups.has(category)) {
			groups.set(category, []);
		}
		groups.get(category).push(field);
	});

	return Array.from(groups.entries())
		.sort(([a], [b]) => a.localeCompare(b, 'et'))
		.map(([category, items]) => ({
			category,
			items: items.sort((a, b) => (a.name_et || '').localeCompare(b.name_et || '', 'et')),
		}));
}

function filterFields(fields, query) {
	const q = query.trim().toLowerCase();
	if (!q) return fields;
	return fields.filter((field) => {
		return (
			field.name_et?.toLowerCase().includes(q) ||
			field.name_en?.toLowerCase().includes(q) ||
			field.name_ru?.toLowerCase().includes(q) ||
			field.category?.toLowerCase().includes(q)
		);
	});
}

function LegalFieldPopover({
	anchorRef,
	wrapRef,
	searchRef,
	query,
	setQuery,
	groupedFields,
	flatFields,
	highlightIndex,
	saving,
	selectedIds,
	t,
	onToggle,
	onMakePrimary,
	onHighlight,
	onStopPropagation,
}) {
	const [position, setPosition] = useState({ top: 0, left: 0 });

	const updatePosition = useCallback(() => {
		const el = anchorRef.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		const popoverWidth = 280;
		const maxLeft = window.innerWidth - popoverWidth - 12;
		setPosition({
			top: rect.bottom + 4,
			left: Math.max(12, Math.min(rect.left, maxLeft)),
		});
	}, [anchorRef]);

	useEffect(() => {
		updatePosition();
		window.addEventListener('scroll', updatePosition, true);
		window.addEventListener('resize', updatePosition);
		return () => {
			window.removeEventListener('scroll', updatePosition, true);
			window.removeEventListener('resize', updatePosition);
		};
	}, [updatePosition, query, groupedFields.length]);

	useEffect(() => {
		searchRef.current?.focus();
	}, [searchRef]);

	const atMax = selectedIds.length >= MAX_FIELDS;

	return ReactDOM.createPortal(
		<div
			ref={wrapRef}
			className="legal-field-popover"
			style={{ position: 'fixed', top: position.top, left: position.left, zIndex: 1060 }}
			onClick={onStopPropagation}
		>
			<div className="legal-field-popover-search">
				<input
					ref={searchRef}
					type="search"
					value={query}
					placeholder={t('project.legalFieldSearch')}
					onChange={(e) => {
						setQuery(e.target.value);
						onHighlight(0);
					}}
					onKeyDown={(e) => {
						if (e.key === 'ArrowDown') {
							e.preventDefault();
							onHighlight((prev) => Math.min(prev + 1, Math.max(flatFields.length - 1, 0)));
						} else if (e.key === 'ArrowUp') {
							e.preventDefault();
							onHighlight((prev) => Math.max(prev - 1, 0));
						} else if (e.key === 'Enter') {
							e.preventDefault();
							const field = flatFields[highlightIndex];
							if (field) onToggle(field);
						}
					}}
				/>
			</div>
			<div className="legal-field-popover-list" role="listbox" aria-multiselectable="true">
				{groupedFields.length === 0 ? (
					<div className="legal-field-popover-empty">{t('project.legalFieldNoResults')}</div>
				) : (
					groupedFields.map(({ category, items }) => (
						<div key={category} className="legal-field-popover-group">
							<div className="legal-field-popover-group-label">{category}</div>
							{items.map((field) => {
								const flatIndex = flatFields.findIndex((item) => item._id === field._id);
								const selectedIndex = selectedIds.indexOf(Number(field.field_id));
								const isSelected = selectedIndex !== -1;
								const isHighlighted = flatIndex === highlightIndex;
								const isPrimary = selectedIndex === 0;
								const disableAdd = saving || (!isSelected && atMax);
								return (
									<div
										key={field._id}
										className={`legal-field-option-row${isSelected ? ' is-selected' : ''}${isHighlighted ? ' is-highlighted' : ''}`}
										onMouseEnter={() => onHighlight(flatIndex)}
									>
										<button
											type="button"
											role="option"
											aria-selected={isSelected}
											className="legal-field-option"
											disabled={disableAdd}
											onClick={() => onToggle(field)}
										>
											<span className="legal-field-option-label">
												{isSelected && (
													<span className="legal-field-option-rank">{selectedIndex + 1}</span>
												)}
												{field.name_et}
											</span>
											{isSelected && <i className="fa-solid fa-check legal-field-option-check" aria-hidden="true" />}
										</button>
										{isSelected && !isPrimary && (
											<button
												type="button"
												className="legal-field-make-primary"
												disabled={saving}
												onClick={() => onMakePrimary(field.field_id)}
											>
												{t('project.legalFieldMakePrimary')}
											</button>
										)}
									</div>
								);
							})}
						</div>
					))
				)}
			</div>
			<div className="legal-field-popover-hint">{t('project.legalFieldMax')}</div>
		</div>,
		document.body,
	);
}

export function LegalFieldPicker({
	fieldIds = [],
	fallbackLabel = '',
	onSave,
	disabled = false,
	t,
}) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [fields, setFields] = useState([]);
	const [loadingFields, setLoadingFields] = useState(false);
	const [saving, setSaving] = useState(false);
	const [highlightIndex, setHighlightIndex] = useState(0);
	const wrapRef = useRef(null);
	const anchorRef = useRef(null);
	const searchRef = useRef(null);

	const selectedIds = useMemo(
		() => (Array.isArray(fieldIds) ? fieldIds.map(Number).filter(Number.isFinite) : []),
		[fieldIds],
	);

	const fieldById = useMemo(() => {
		const map = new Map();
		fields.forEach((field) => map.set(Number(field.field_id), field));
		return map;
	}, [fields]);

	const filteredFields = useMemo(() => filterFields(fields, query), [fields, query]);
	const groupedFields = useMemo(() => groupFields(filteredFields), [filteredFields]);
	const flatFields = useMemo(() => groupedFields.flatMap((group) => group.items), [groupedFields]);

	const loadFields = useCallback(async () => {
		if (fields.length > 0 || loadingFields) return;
		setLoadingFields(true);
		try {
			const response = await axios.get('/legal-fields/active');
			setFields(response?.data || []);
		} finally {
			setLoadingFields(false);
		}
	}, [fields.length, loadingFields]);

	useEffect(() => {
		if (selectedIds.length > 0 || fallbackLabel) {
			loadFields();
		}
	}, [selectedIds.length, fallbackLabel, loadFields]);

	useEffect(() => {
		if (!open) return undefined;
		loadFields();
		const handleClickOutside = (e) => {
			const inWrap = wrapRef.current?.contains(e.target);
			const inAnchor = anchorRef.current?.contains(e.target);
			if (!inWrap && !inAnchor) {
				setOpen(false);
				setQuery('');
				setHighlightIndex(0);
			}
		};
		const handleEscape = (e) => {
			if (e.key === 'Escape') {
				setOpen(false);
				setQuery('');
				setHighlightIndex(0);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleEscape);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleEscape);
		};
	}, [open, loadFields]);

	useEffect(() => {
		if (highlightIndex >= flatFields.length) {
			setHighlightIndex(Math.max(flatFields.length - 1, 0));
		}
	}, [flatFields.length, highlightIndex]);

	const persist = async (nextIds) => {
		setSaving(true);
		try {
			await onSave(nextIds);
		} finally {
			setSaving(false);
		}
	};

	const handleToggleOpen = (e) => {
		if (disabled || saving) return;
		e.stopPropagation();
		setOpen((prev) => {
			if (prev) {
				setQuery('');
				setHighlightIndex(0);
			}
			return !prev;
		});
	};

	const handleToggleField = async (field) => {
		if (!field || saving) return;
		const id = Number(field.field_id);
		const index = selectedIds.indexOf(id);
		let next;
		if (index !== -1) {
			next = selectedIds.filter((item) => item !== id);
		} else {
			if (selectedIds.length >= MAX_FIELDS) return;
			next = [...selectedIds, id];
		}
		await persist(next);
	};

	const handleMakePrimary = async (fieldId) => {
		if (saving) return;
		const id = Number(fieldId);
		const rest = selectedIds.filter((item) => item !== id);
		await persist([id, ...rest]);
	};

	const chips = selectedIds
		.map((id, index) => {
			const field = fieldById.get(id);
			return {
				id,
				index,
				label: field?.name_et || (index === 0 && fallbackLabel) || String(id),
			};
		})
		.filter(Boolean);

	const showFallback = chips.length === 0 && fallbackLabel;
	const isPlaceholder = chips.length === 0 && !fallbackLabel;

	return (
		<div className="legal-field-picker">
			<div
				ref={anchorRef}
				className={`legal-field-chips${disabled || saving ? ' legal-field-chips--disabled' : ''}${open ? ' legal-field-chips--open' : ''}`}
			>
				{isPlaceholder && (
					<button
						type="button"
						className="legal-field-chip legal-field-chip--placeholder"
						disabled={disabled || saving}
						aria-haspopup="listbox"
						aria-expanded={open}
						onClick={handleToggleOpen}
					>
						{t('project.setLegalField')}
						{!disabled && <i className="fa-solid fa-chevron-down legal-field-chip-caret" aria-hidden="true" />}
					</button>
				)}
				{showFallback && (
					<button
						type="button"
						className="legal-field-chip"
						disabled={disabled || saving}
						aria-haspopup="listbox"
						aria-expanded={open}
						onClick={handleToggleOpen}
					>
						{fallbackLabel}
						{!disabled && <i className="fa-solid fa-chevron-down legal-field-chip-caret" aria-hidden="true" />}
					</button>
				)}
				{chips.map((chip) => (
					<button
						key={chip.id}
						type="button"
						className="legal-field-chip"
						disabled={disabled || saving}
						aria-haspopup="listbox"
						aria-expanded={open}
						onClick={handleToggleOpen}
					>
						{chip.label}
						{!disabled && (
							<i className="fa-solid fa-chevron-down legal-field-chip-caret" aria-hidden="true" />
						)}
					</button>
				))}
			</div>
			{open && !disabled && (
				<LegalFieldPopover
					anchorRef={anchorRef}
					wrapRef={wrapRef}
					searchRef={searchRef}
					query={query}
					setQuery={setQuery}
					groupedFields={groupedFields}
					flatFields={flatFields}
					highlightIndex={highlightIndex}
					saving={saving || loadingFields}
					selectedIds={selectedIds}
					t={t}
					onToggle={handleToggleField}
					onMakePrimary={handleMakePrimary}
					onHighlight={setHighlightIndex}
					onStopPropagation={(e) => e.stopPropagation()}
				/>
			)}
		</div>
	);
}

export function LegalFieldAiClassify({
	proposal,
	loading,
	error,
	onClassify,
	onConfirm,
	onDismiss,
	disabled = false,
	t,
}) {
	const wrapRef = useRef(null);
	const [open, setOpen] = useState(false);

	const hasPanel = Boolean(proposal || error);
	const results = proposal?.results || [];
	const selectedFields = proposal?.selectedFields || [];
	const droppedFields = proposal?.droppedFields || [];
	const selectedIds = new Set(selectedFields.map((field) => Number(field.field_id)));
	const canConfirm = selectedFields.length > 0 || results.length > 0;

	useEffect(() => {
		if (hasPanel) {
			setOpen(true);
		}
	}, [hasPanel]);

	useEffect(() => {
		if (!open) return undefined;
		const handleClickOutside = (e) => {
			if (!wrapRef.current?.contains(e.target)) {
				setOpen(false);
				onDismiss();
			}
		};
		const handleEscape = (e) => {
			if (e.key === 'Escape') {
				setOpen(false);
				onDismiss();
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleEscape);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleEscape);
		};
	}, [open, onDismiss]);

	const fmtPct = (value) => {
		if (value == null || Number.isNaN(Number(value))) return '—';
		return `${Math.round(Number(value) * 100)}%`;
	};

	const handleClassify = () => {
		setOpen(false);
		onClassify();
	};

	return (
		<div className="legal-field-ai legal-field-ai--header" ref={wrapRef}>
			<button
				type="button"
				className="button button-light"
				disabled={disabled || loading}
				onClick={handleClassify}
			>
				{loading ? t('common.loading') : t('project.classifyFields')}
			</button>
			{open && hasPanel && (
				<div className="legal-field-ai-panel" role="dialog" aria-label={t('project.classifyFieldsProposal')}>
					{proposal?.profileName && (
						<p className="legal-field-ai-meta">
							{t('project.classifyFieldsMeta', { name: proposal.profileName })}
						</p>
					)}
					{!proposal?.profileName && !error && (
						<p className="legal-field-ai-meta">{t('project.classifyFieldsSource')}</p>
					)}
					{error && <p className="legal-field-ai-error">{error}</p>}
					{proposal && (
						<>
							<div className="legal-field-ai-proposal-title">{t('project.classifyFieldsProposal')}</div>
							{results.length === 0 ? (
								<p className="legal-field-ai-error">{t('project.classifyFieldsNone')}</p>
							) : (
								<ul className="legal-field-ai-list">
									{results.map((field) => {
										const isSelected = selectedIds.has(Number(field.field_id));
										const dropped = droppedFields.find((item) => Number(item.field_id) === Number(field.field_id));
										let suffix = '';
										if (isSelected) suffix = ' ✓';
										else if (dropped) {
											suffix = ` (${t(`match.dropReason.${dropped.dropReason || 'count_limit'}`)})`;
										}
										return (
											<li key={field.field_id}>
												{field.name}
												{' '}
												({fmtPct(field.confidence)})
												{suffix}
											</li>
										);
									})}
								</ul>
							)}
							<div className="legal-field-ai-actions">
								{canConfirm && (
									<button type="button" className="btn btn-sm btn-primary" onClick={onConfirm} disabled={loading}>
										{t('project.classifyFieldsConfirm')}
									</button>
								)}
								<button type="button" className="btn btn-sm btn-secondary" onClick={() => { setOpen(false); onDismiss(); }} disabled={loading}>
									{t('project.classifyFieldsDismiss')}
								</button>
							</div>
						</>
					)}
					{error && !proposal && (
						<div className="legal-field-ai-actions">
							<button type="button" className="btn btn-sm btn-secondary" onClick={() => { setOpen(false); onDismiss(); }} disabled={loading}>
								{t('project.classifyFieldsDismiss')}
							</button>
						</div>
					)}
				</div>
			)}
		</div>
	);
}
